import type {QueryClient} from '@tanstack/react-query'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {createMemexItemModel} from '../../../models/memex-item-model'
import {buildInitialItemsAndColumns} from '../memex-items-data'
import {
  getActiveQueriesMetadataForVariables,
  hasMemexItemsQueryData,
  setActiveQueriesMetadataForVariables,
  setQueryDataForMemexItemGroupsPage,
  setQueryDataForMemexItemsPage,
} from '../query-client-api'
import type {GroupId} from './query-keys'
import {buildActiveQueriesMetadataQueryKey, groupIdForUngroupedData} from './query-keys'
import {
  type ActiveQueriesMetadata,
  type MemexItemGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  type PaginatedMemexItemsQueryVariables,
} from './types'

type UseMemexItemsActiveQueriesMetadataReturnType = {
  activeQueriesMetadata: ActiveQueriesMetadata
  getActiveQueriesMetadata: () => ActiveQueriesMetadata
  setActiveQueriesMetadata: (newActiveQueriesMetaData: ActiveQueriesMetadata) => void
}

/**
 * A thin API around the react-query `useQuery` that represents which query keys should be considered
 * active based on pagination data we've loaded.
 * @param variables Used in the query key for our active query data. Ensures that
 * the data is reset when we change the request parameters for the active sort, filter, etc.
 * @returns callbacks for interacting with the active query query data.
 */
export function useMemexItemsActiveQueriesMetadata(
  variables: PaginatedMemexItemsQueryVariables,
): UseMemexItemsActiveQueriesMetadataReturnType {
  const queryClient = useQueryClient()

  let initialActiveQueryData: ActiveQueriesMetadata = {pageParams: [pageParamForInitialPage]}

  // If we have not yet read any data into the queryClient, then go ahead and
  // read data from the JSON island. We'll use this data to populate our
  // active queries query data, as well as setting up the initial queries for each
  // group (or a single query representing all items in the JSON island if we're ungrouped)
  // This data is then provided as `initialData` for our `useQuery` below.
  if (!hasMemexItemsQueryData(queryClient)) {
    const initialPages = getInitialPages(queryClient, variables)
    initialActiveQueryData = buildInitialActiveQueriesMetadata(initialPages)

    for (const page of initialPages) {
      setQueryDataForMemexItemsPage(queryClient, variables, page.groupId, page.pageParam, page.queryData)
    }
  }

  const {data: activeQueriesMetadata} = useQuery({
    queryKey: buildActiveQueriesMetadataQueryKey(variables),
    initialData: initialActiveQueryData,
    enabled: false,
  })

  const setActiveQueriesMetadata = useCallback(
    (newQueryData: ActiveQueriesMetadata) => {
      setActiveQueriesMetadataForVariables(queryClient, variables, newQueryData)
    },
    [queryClient, variables],
  )

  const getActiveQueriesMetadata = useCallback(() => {
    return getActiveQueriesMetadataForVariables(queryClient, variables)
  }, [queryClient, variables])

  return {activeQueriesMetadata, setActiveQueriesMetadata, getActiveQueriesMetadata}
}

type InitialPageData = {
  groupId: GroupId
  pageParam: PageParam
  queryData: MemexItemsPageQueryData
}

/**
 * Read data from the JSON island and create our initial query key / page pairs.
 * The query key will be a groupId+pageParam combination for grouped data, and just a pageParam
 * for ungrouped data.
 * For grouped data, we will also set up the query data for the MemexItemGroupsPage query in the
 * query client.
 */
function getInitialPages(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
): Array<InitialPageData> {
  const {memexItems} = buildInitialItemsAndColumns('memex-paginated-items-data')
  if ('groups' in memexItems) {
    const pages = memexItems.groups.map(group => {
      return {
        groupId: group.groupId,
        pageParam: pageParamForInitialPage,
        queryData: {
          nodes: group.items.nodes.map(item => createMemexItemModel(item)),
          pageInfo: group.items.pageInfo,
        },
      }
    })
    const queryDataForGroups: MemexItemGroupsPageQueryData = {
      groups: memexItems.groups.map(group => ({groupId: group.groupId, groupName: group.groupName})),
      pageInfo: memexItems.pageInfo,
    }
    setQueryDataForMemexItemGroupsPage(queryClient, variables, pageParamForInitialPage, queryDataForGroups)
    return pages
  } else {
    const initialItems = memexItems.nodes || []
    const firstPage = {
      ...memexItems,
      nodes: initialItems.map(item => createMemexItemModel(item)),
    }
    return [{groupId: groupIdForUngroupedData, pageParam: pageParamForInitialPage, queryData: firstPage}]
  }
}

/**
 * Given a set of queryKey / page pairs, set up the initial active query data object.
 * If the queryKey represents a group, then we will have grouped data, otherwise
 * the data will be ungrouped.
 */
function buildInitialActiveQueriesMetadata(initialPages: Array<InitialPageData>): ActiveQueriesMetadata {
  if (initialPages[0]?.groupId == null) {
    const pageParams = initialPages.map(page => page.pageParam)
    return {pageParams}
  } else {
    // This is assuming that we won't have more than 1 initial page per group.
    // If that assumption no longer holds, then we'll need to update this
    // code to handle that case.
    const groups = initialPages.map(page => ({
      groupId: page.groupId || '',
      pageParams: [page.pageParam],
    }))
    return {groups, pageParams: [pageParamForInitialPage]}
  }
}
